import React, { useEffect, useState, useContext } from 'react';
import ProfileLayout from '../../components/profile-layout';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import { useTranslation } from '../../hooks/useTranslation';
import AccountDropdown from '../../components/account-dropdown';
import {
  ManageAlertsLanding,
  ManageAlertsIneligible,
  useManageAlerts,
  ManageAlertsSuccess,
  AlertsModal,
} from '../../components/account-summary/manage-alerts';
import Backdrop from '../../components/backdrop';
import { Router, navigate, useLocation } from '@reach/router';
import ROUTES from '../../routes';
import { Model } from '../../components/account-summary/manage-alerts/types';
import useWrapWithLoader from '../../hooks/useWrapWithLoading';
import {
  LinkedPerson,
  UpdateValidatePhoneError,
} from '../../__generated__/pge-types';
import { NotificationsContext } from '../../providers/NotificationsProvider';
import useAccountCustomer from '../../hooks/useAccountCustomer';
import Helmet from 'react-helmet';
import { displayPhoneNumberFormat, parsePhoneNumber } from '../../util/format';
import useSelectedAccountParams from '../../hooks/useSelectedAccountParams';

export default function ManageAlertsPage(_: { path?: string }) {
  const { t } = useTranslation();
  const { accountParams } = useSelectedAccountParams();
  const [selectedPerson, setSelectedPerson] = useState<
    LinkedPerson | undefined
  >();
  const [updateAlertsPhoneNumber, setUpdateAlertsPhoneNumber] = useState<
    string | undefined
  >(undefined);
  const [smsStopPhoneNumber, setSmsStopPhoneNumber] = useState<
    string | undefined
  >(undefined);
  const [contactType, setContactType] = useState<string | undefined>(undefined);
  const [showMobTextField, setMobTextField] = useState(false);
  const {
    loading,
    alerts,
    form,
    defaultFormValues,
    showWeeklyUsageAlertsText,
    setShowWeeklyUsageAlertsText,
    showOutageNotificationAlertsText,
    setShowOutageNotificationAlertsText,
    account,
    service,
    refetchAccount,
    personList,
    handleChangePersonDetails,
    handleDeletePersonDetails,
    displayError,
    refetchPerson,
    customer,
    mainPersonEmail,
    getAlertService,
    isSmsEnrollmentDisabled,
    smsStopAlertType,
    smsStopNumbers,
  } = useManageAlerts({
    params: {
      selectedPersonParams: selectedPerson ? selectedPerson : null,
      phoneNumber: updateAlertsPhoneNumber,
      smsStopPhoneNumber,
    },
  });
  const { refetch: refetchAccountInfo } = useAccountCustomer();
  const [selectedContactValue, setSelectedContactValue] = useState<
    string | undefined
  >(undefined);
  const { wrapWithLoader } = useWrapWithLoader();
  const notificationsContext = useContext(NotificationsContext);
  const location = useLocation();

  const [encryptedActivationCode, setEncryptedActivationCode] = useState<
    string | null
  >(null);

  const [encryptedContactId, setEncryptedContactId] = useState<
    string | null | undefined
  >(null);
  const [encryptedEmailId, setEncryptedEmailId] = useState<
    string | null | undefined
  >(null);
  const [recentlyAddedPerson, setRecentlyAddedPerson] = useState();
  const [alertSuccess, setAlertSuccess] = useState(false);
  const [selectedContactType, setSelectedContactType] = useState<
    string | undefined
  >(undefined);

  const [isDropdownInitiallySelected, setDropdownInitiallySelected] = useState<
    boolean
  >(false);
  const [isExecuted, setIsExecuted] = useState<boolean>(false);
  const [hasInitialAccountFetch, setHasInitialAccountFetch] = useState<boolean>(
    false,
  );
  const selectedPersonIdentity =
    selectedPerson?.encryptedPersonId || selectedPerson?.personId;

  useEffect(() => {
    setUpdateAlertsPhoneNumber(undefined);
    setSmsStopPhoneNumber(undefined);
    setSelectedContactValue(undefined);
    setSelectedContactType(undefined);
    setContactType(undefined);
    setMobTextField(false);
    setDropdownInitiallySelected(false);
    setIsExecuted(false);
  }, [
    accountParams?.accountNumber,
    selectedPersonIdentity,
    selectedPerson?.verificationStatus,
  ]);

  useEffect(() => {
    const run = async () => {
      if (!isExecuted && selectedContactValue && isDropdownInitiallySelected) {
        setIsExecuted(true);
        void (await form.setValue(
          'phoneNumber',
          parsePhoneNumber(selectedContactValue),
        ));
      }
    };
    void run();
  }, [isDropdownInitiallySelected, isExecuted, selectedContactValue]);
  useEffect(() => {
    if (account && !account?.isLoggedInUserOnAccount) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      navigate(ROUTES.MANAGE_ALERTS_INELIGIBLE);
    } else if (
      account &&
      account?.isLoggedInUserOnAccount &&
      location.pathname !== ROUTES.MANAGE_ALERTS
    ) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      navigate(ROUTES.MANAGE_ALERTS);
    }
  }, [account]);

  useEffect(() => {
    if (customer && personList !== undefined) {
      const person: any = personList?.find(
        (personD: any) => personD?.personId === customer?.personId,
      );
      setSelectedPerson(person);
    }
    return () => {
      setSelectedPerson(undefined);
    };
  }, [personList, customer]);

  useEffect(() => {
    if (recentlyAddedPerson) {
      const { personId } = recentlyAddedPerson as any;
      const person: any = personList?.find(
        (p: any) => p?.personId === personId,
      );
      if (person) {
        setSelectedPerson(person);
        setRecentlyAddedPerson(undefined);
      }
    }
  }, [recentlyAddedPerson, personList]);

  useEffect(() => {
    if (!hasInitialAccountFetch && refetchAccountInfo && !loading) {
      setHasInitialAccountFetch(true);
      void refetchAccountInfo();
    }
  }, [loading, hasInitialAccountFetch, refetchAccountInfo]);

  function handleIneligibleSubmit() {
    return navigate(ROUTES.ACCOUNT);
  }

  const handleLandingPhoneSubmit = wrapWithLoader(async (data: Model) => {
    if (
      data.phoneNumber
      // &&
      // data.phoneNumber !== defaultFormValues.phoneNumber
    ) {
      const {
        encryptedActivationCode: encryptedCode,
      } = await service.sendActivationCode(data.phoneNumber);
      setEncryptedActivationCode(encryptedCode);
    }
  });

  const handleChangePerson = wrapWithLoader(async (selectedPersonData: any) => {
    setSelectedPerson(selectedPersonData);
    setUpdateAlertsPhoneNumber(undefined);
    setSmsStopPhoneNumber(undefined);
    setSelectedContactValue(undefined);
    setSelectedContactType(undefined);
    setContactType(undefined);
    await handleChangePersonDetails();
  });

  const handleDeletePerson = wrapWithLoader(async () => {
    await handleDeletePersonDetails(form.values, selectedPerson!);
  });
  const getContactType = () => {
    const phoneNumber = displayPhoneNumberFormat(defaultFormValues.phoneNumber);
    return customer?.contactDetails?.filter(
      contact => contact?.contactValue === phoneNumber,
    )[0]?.contactType;
  };
  const handleConfirmPhoneSubmit = wrapWithLoader(
    async (
      activationCode: string,
      onInvalid: () => void,
      onSuccess: () => void,
    ) => {
      const {
        success,
        error,
        encryptedContactId: encryptContactId,
        isAlertSelected,
      } = await service.validateandUpdatePhoneNumber(
        activationCode,
        String(encryptedActivationCode),
        form.values,
        defaultFormValues,
        contactType || getContactType(),
      );

      if (!success && error === UpdateValidatePhoneError.InvalidCode) {
        return onInvalid();
      } else if (
        (!success && error === UpdateValidatePhoneError.ContactExists) ||
        (!success &&
          error === UpdateValidatePhoneError.PhoneAttachedToActiveAccount)
      ) {
        form.setError('phoneNumber', t('MANAGE_ALERTS_BAD_PHONE_NUMBER'));
        return navigate(ROUTES.MANAGE_ALERTS);
      } else {
        if (isAlertSelected) {
          setEncryptedContactId(encryptContactId);
          setSmsStopPhoneNumber(form.values.phoneNumber);
          return navigate(ROUTES.MANAGE_ALERTS_CONFIRM);
        } else {
          await refetchAccount();
          await getAlertService();
          const isSelectedContactValuePrimary = customer?.contactDetails?.filter(
            fill =>
              fill?.contactValue ===
              displayPhoneNumberFormat(selectedContactValue),
          )?.[0]?.isPrimary;
          if (!(isSelectedContactValuePrimary || selectedContactValue)) {
            setUpdateAlertsPhoneNumber(form.values.phoneNumber);
          } else {
            setUpdateAlertsPhoneNumber(
              isSelectedContactValuePrimary || !selectedContactValue
                ? form.values.phoneNumber
                : defaultFormValues.phoneNumber,
            );
          }
          setSmsStopPhoneNumber(form.values.phoneNumber);
          setSelectedContactType(undefined);
          setSelectedContactValue(undefined);
          return onSuccess();
        }
      }
    },
  );

  function handleTooManyFailures() {
    setEncryptedActivationCode(null);
    return navigate(ROUTES.MANAGE_ALERTS);
  }

  const handleConfirmSubmit = wrapWithLoader(async () => {
    const { error, removedPhoneNumber, success } = await service.updateAlerts(
      form.values,
      defaultFormValues,
      encryptedContactId!,
      encryptedEmailId!,
      updateAlertsPhoneNumber,
    );

    if (success) {
      await refetchAccount();
      setAlertSuccess(true);

      if (removedPhoneNumber) {
        notificationsContext.setState({
          isOpen: true,
          severity: 'success',
          message: t('PHONE_NUMBER_REMOVED'),
        });
      }
    } else if (error || success === false) {
      return navigate(ROUTES.MANAGE_ALERTS);
    } else {
      return Promise.reject('Failed to update alerts');
    }
  });

  return (
    <>
      {alertSuccess && (
        <ManageAlertsSuccess
          isOpen={alertSuccess}
          onClose={() => {
            setAlertSuccess(false);
          }}
        />
      )}
      <Helmet>
        <title>{t('MANAGE_USERS_AND_ALERTS_TITLE')}</title>
      </Helmet>
      <ProfileLayout dense={false}>
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <Box marginBottom={2}>
              <Typography variant="h1">
                {t('MANAGE_USERS_AND_ALERTS_TITLE')}
              </Typography>
            </Box>
          </Grid>

          <Grid item>
            <AccountDropdown />
          </Grid>
          <Grid item css={{ maxWidth: '100%' }}>
            {loading && <Backdrop forceOpen />}
            {!loading && (
              <Router basepath="/">
                <ManageAlertsLanding
                  path={ROUTES.MANAGE_ALERTS}
                  form={form}
                  alerts={alerts}
                  selectedContactValue={selectedContactValue}
                  setSelectedContactValue={setSelectedContactValue}
                  setMobTextField={setMobTextField}
                  showMobTextField={showMobTextField}
                  updatesAlertsInfo={setUpdateAlertsPhoneNumber}
                  updateSmsStopPhoneNumber={setSmsStopPhoneNumber}
                  defaultFormValues={defaultFormValues}
                  onSubmit={handleConfirmSubmit}
                  onPhoneSubmit={handleLandingPhoneSubmit}
                  personList={personList}
                  handleChangePerson={handleChangePerson}
                  selectedPerson={selectedPerson}
                  handleDeletePerson={handleDeletePerson}
                  displayError={displayError}
                  loggedInPersonId={customer?.personId}
                  refetchPerson={refetchPerson}
                  setRecentlyAddedPerson={setRecentlyAddedPerson}
                  mainPersonEmail={mainPersonEmail}
                  setSelectedContactType={setSelectedContactType}
                  selectedContactType={selectedContactType}
                  setDropdownInitiallySelected={setDropdownInitiallySelected}
                  isDropdownInitiallySelected={isDropdownInitiallySelected}
                  loggedInPersonType={
                    personList?.find(
                      per => per?.personId === customer?.personId,
                    )?.accountRelTypeCd
                  }
                  loggedInPersonName={customer?.personName}
                  handleConfirmPhoneSubmit={handleConfirmPhoneSubmit}
                  onTooManyFailures={handleTooManyFailures}
                  updateContactType={setContactType}
                  isSmsEnrollmentDisabled={isSmsEnrollmentDisabled}
                  smsStopAlertType={smsStopAlertType}
                  smsStopNumbers={smsStopNumbers}
                />
                <ManageAlertsIneligible
                  path={ROUTES.MANAGE_ALERTS_INELIGIBLE}
                  onSubmit={handleIneligibleSubmit}
                />
              </Router>
            )}
          </Grid>
        </Grid>
        <AlertsModal
          open={showWeeklyUsageAlertsText}
          onClose={() => setShowWeeklyUsageAlertsText(false)}
          title={t('USAGE_TITLE')}
          bodyText={[
            t('WEEKLY_NOTIFICATION_DATES'),
            t('WEEKLY_USAGE_NOTIFICATION'),
          ]}
        />
        <AlertsModal
          open={showOutageNotificationAlertsText}
          onClose={() => setShowOutageNotificationAlertsText(false)}
          title={t('OUTAGE_NOTIFICATIONS')}
          bodyText={
            account?.commPreferences
              ?.find(a => a?.notificationType === 'OUT')
              ?.deliveryTypeDetails?.find(a1 => a1?.deliveryType === 'SMS')
              ?.contactDetails?.some(con => con?.isSelected)
              ? [t('OUTAGE_NOTIFICATION_TOOLTIP_NOT_ENROLLED_USER')]
              : [t('OUTAGE_NOTIFICATION_TOOLTIP_ENROLLED_USER')]
          }
        />
      </ProfileLayout>
    </>
  );
}
